import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators'; // Ajoutez ceci
import { SelectOptionsService } from '../../../core/services/select-options.service';

/**
 * Option d'un sélecteur. `id` accepte aussi une chaîne : certains champs sont
 * des énumérations textuelles (`'published'`, `'easy'`…) et non des clés.
 */
export interface SelectOption {
  id: number | string;
  title: string;
}

@Component({
  selector: 'app-custom-select',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './custom-select.component.html',
  styleUrls: ['./custom-select.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomSelectComponent),
      multi: true,
    },
  ],
})
export class CustomSelectComponent
  implements ControlValueAccessor, OnInit, OnChanges, OnDestroy
{
  @Input() label: string = '';
  @Input() placeholderText: string = 'Select an option';
  @Input() options: SelectOption[] = [];
  @Input() allowAdd: boolean = true;
  @Input() dir: 'ltr' | 'rtl' = 'ltr';
  @Input() errorMessage: string = '';
  @Input() showError: boolean | undefined = false;
  @Input() inputId: string = 'custom-select';
  @Input() labelClass: string = 'form-label fs-19 fg-400 mb-3';
  @Input() triggerClass: string = 'select-trigger bg-input p-3 border-0';
  @Input() multiple: boolean = false;
  @Input() disabled: boolean = false;
  /**
   * En deçà de ce nombre d'options, le champ de recherche est masqué :
   * filtrer trois entrées visibles d'un coup d'œil n'apporte rien.
   */
  @Input() searchThreshold: number = 8;

  @Output() optionAdded = new EventEmitter<SelectOption>();
  @Output() optionAddError = new EventEmitter<any>(); // Nouvel événement pour les erreurs
  /**
   * Sélection **par l'utilisateur** (pas les écritures programmatiques du
   * formulaire) : c'est l'équivalent du `(change)` d'un `<select>` natif.
   */
  @Output() selectionChange = new EventEmitter<number | string | null>();

  @ViewChild('selectContainer') selectContainer!: ElementRef;
  @ViewChild('searchInput') searchInput!: ElementRef;

  isOpen: boolean = false;
  searchTerm: string = '';
  filteredOptions: SelectOption[] = [];
  selectedValue: number | string | null = null;
  selectedValues: Array<number | string> = [];
  isAddingOption: boolean = false; // Indicateur de chargement

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private selectOptionsService: SelectOptionsService) {} // Injectez le service

  ngOnInit(): void {
    this.filteredOptions = [...this.options];
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Les options arrivent souvent après l'initialisation (chargement du
    // référentiel, cascade parcours → matière) : sans cette resynchronisation,
    // la liste resterait vide jusqu'à un cycle ouverture/fermeture.
    if (changes['options']) {
      this.searchTerm = '';
      this.filteredOptions = [...this.options];
    }
  }

  ngOnDestroy(): void {}

  /** Le champ de recherche n'apparaît que si la liste est assez longue. */
  get showSearch(): boolean {
    return this.allowAdd || this.options.length >= this.searchThreshold;
  }

  /** `0` et `''` sont des valeurs légitimes : ne pas les traiter comme « vide ». */
  get hasValue(): boolean {
    return this.selectedValue !== null && this.selectedValue !== undefined;
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    if (this.multiple) {
      this.selectedValues = Array.isArray(value) ? value : (value ? [value] : []);
    } else {
      this.selectedValue = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) this.closeDropdown();
  }

  // Toggle dropdown
  toggleDropdown(): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      // La liste peut avoir changé depuis la dernière ouverture.
      this.searchTerm = '';
      this.filteredOptions = [...this.options];
      setTimeout(() => {
        this.searchInput?.nativeElement.focus();
      }, 100);
    } else {
      this.searchTerm = '';
      this.filteredOptions = [...this.options];
    }
  }

  // Search functionality
  onSearch(): void {
    const term = this.searchTerm.toLowerCase().trim();
    if (!term) {
      this.filteredOptions = [...this.options];
      return;
    }

    this.filteredOptions = this.options.filter((option) =>
      option.title.toLowerCase().includes(term),
    );
  }

  // Select an option
  selectOption(option: SelectOption): void {
    if (this.multiple) {
      this.toggleSelection(option.id);
    } else {
      this.selectedValue = option.id;
      this.onChange(option.id);
      this.onTouched();
      this.selectionChange.emit(option.id);
      this.closeDropdown();
    }
  }

  // Toggle selection for multi-select
  toggleSelection(id: number | string): void {
    const index = this.selectedValues.indexOf(id);
    if (index > -1) {
      this.selectedValues = this.selectedValues.filter(v => v !== id);
    } else {
      this.selectedValues = [...this.selectedValues, id];
    }
    this.onChange(this.selectedValues);
    this.onTouched();
  }

  // Check if option is selected (for multi-select)
  isSelected(id: number | string): boolean {
    if (this.multiple) {
      return this.selectedValues.includes(id);
    }
    return this.selectedValue === id;
  }

  // Remove a selected item (for multi-select tags)
  removeSelection(id: number | string, event: MouseEvent): void {
    event.stopPropagation();
    this.selectedValues = this.selectedValues.filter(v => v !== id);
    this.onChange(this.selectedValues);
    this.onTouched();
  }

  // Get selected options for multi-select display
  getSelectedOptions(): SelectOption[] {
    return this.options.filter(opt => this.selectedValues.includes(opt.id));
  }

  // Get selected label
  getSelectedLabel(): string {
    const selected = this.options.find((opt) => opt.id === this.selectedValue);
    return selected ? selected.title : '';
  }

  // Check if exact match exists
  exactMatchExists(): boolean {
    const term = this.searchTerm.toLowerCase().trim();
    return this.options.some((opt) => opt.title.toLowerCase() === term);
  }

  // Add new option with API call
  addNewOption(): void {
    if (!this.searchTerm.trim() || this.isAddingOption) return;

    this.isAddingOption = true;
    const newTitle = this.searchTerm.trim();

    this.selectOptionsService
      .createOption(newTitle)
      .pipe(
        finalize(() => {
          this.isAddingOption = false;
        }),
      )
      .subscribe({
        next: (newOption: SelectOption) => {
          // Ajouter l'option retournée par l'API à la liste
          this.options.push(newOption);
          this.filteredOptions = [...this.options];

          // Émettre l'événement avec la nouvelle option
          this.optionAdded.emit(newOption);

          // Sélectionner la nouvelle option
          this.selectOption(newOption);
        },
        error: (error) => {
          console.error("Erreur lors de l'ajout de l'option:", error);
          // Émettre l'événement d'erreur
          this.optionAddError.emit(error);

          // Optionnel : afficher un message d'erreur à l'utilisateur
          // this.errorMessage = 'Impossible d\'ajouter l\'option';
          // this.showError = true;
        },
      });
  }

  // Close dropdown
  closeDropdown(): void {
    this.isOpen = false;
    this.searchTerm = '';
    this.filteredOptions = [...this.options];
  }

  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.selectContainer &&
      !this.selectContainer.nativeElement.contains(event.target)
    ) {
      this.closeDropdown();
    }
  }
}
